// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";

// Components
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { CheckCircleIcon, PlusIcon } from "src/assets/icons";

import { DEFAULT_EXAM_CARDS, DefaultExamCardType } from "src/constants/enums";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Styles
import { ExamSliderButton } from "src/styles/mui/muiStylesHelper";

type SelectSimulationButtonType = {
  item: DefaultExamCardType;
};

const SelectSimulationButton = ({ item }: SelectSimulationButtonType) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { clinicInfo } = useClinicContext();
  const { selectedExamCards, actions } = useExamSliderContext();
  const { setSelectedExamCards } = actions;

  const canUseExamPackaging = clinicInfo?.licensing?.canUseExamPackaging;
  const maxCardArrayLength = canUseExamPackaging ? 5 : 1;

  const label = useMemo(
    () => t("buttonLabel" in item ? item.buttonLabel : item.label),
    [t, item]
  );

  const isSelected = useMemo(
    () =>
      Boolean(
        selectedExamCards.find(
          (card) => card.examFieldType === item.algorithmPath
        )
      ),
    [selectedExamCards, item.algorithmPath]
  );

  const isDisabledButton = useMemo(
    () => selectedExamCards.length >= maxCardArrayLength || isSelected,
    [selectedExamCards, isSelected, maxCardArrayLength]
  );

  const numOfSelectedExams = useMemo(() => (isSelected ? 1 : 0), [isSelected]);

  // A simulation already added to the package shows as checked (dark,
  // filled) rather than as an addable "+" button.
  const isChecked = numOfSelectedExams > 0;

  const onSelectExam = useCallback(() => {
    if (isDisabledButton) return;

    setSelectedExamCards((prev: Array<SelectedExamCardType>) => {
      const defaultCard = DEFAULT_EXAM_CARDS.find(
        (card) => card.examFieldType === item.algorithmPath
      );

      const examNumLabel = selectedExamCards.filter(
        (card) => card.examFieldType === item.algorithmPath
      )?.length;

      return [
        ...prev,
        {
          ...defaultCard,
          examNumLabel: examNumLabel + 1,
          groupId: selectedExamCards?.length,
        },
      ];
    });
  }, [
    isDisabledButton,
    selectedExamCards,
    item.algorithmPath,
    setSelectedExamCards,
  ]);

  return (
    <Button
      data-testid={`${item.algorithmPath}-select-button`}
      disabled={isDisabledButton}
      startIcon={
        isChecked ? (
          <Box color="#22BE39">
            <CheckCircleIcon width="16px" height="16px" />
          </Box>
        ) : (
          <PlusIcon width="16px" height="16px" />
        )
      }
      onClick={onSelectExam}
      sx={ExamSliderButton(theme, isChecked)}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          minWidth: 0,
          gap: "8px",
        }}
      >
        <Typography
          fontSize="12px"
          noWrap
          sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {label}
        </Typography>
        <Typography fontSize="12px" sx={{ flexShrink: 0 }}>
          {isChecked ? `${t("Qty")}: ${numOfSelectedExams}` : ""}
        </Typography>
      </Box>
    </Button>
  );
};

export default SelectSimulationButton;
